import { ReactElement, createContext, useCallback, useContext, useEffect, useState } from 'react';
import { useGetUserDetailsQuery, useGetUserDetailsLazyQuery } from './user-details.graphql';
import {
  validateApolloResponse,
  ValidUserDetails,
  WorkspaceRoleEnum,
  WorkspaceType,
} from './user-details-validator';
import { useOnIdTokenChangedObserver } from '@modules/auth-helpers';
import { logger } from '@modules/logger';

type ProfileDetailsContextProps = {
  userDetails: ValidUserDetails & {
    helpers: {
      isTeamAdmin: boolean;
    };
  };
  refetch: () => Promise<ValidUserDetails>;
};
// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
export const UserDetailsContext = createContext<ProfileDetailsContextProps>(undefined!);

export const useUserDetails = (): ProfileDetailsContextProps => {
  const context = useContext(UserDetailsContext);

  if (context === undefined || context === null) {
    throw new Error('useUserDetails can only be used on authenticated routes with non-anonymous users');
  }

  return context;
};

// Used solely for shared components that may or may not have an authed user, do not use, just in the interim
export const useOptionalUserDetails = (): ValidUserDetails | null => {
  const [userDetails, setUserDetails] = useState<null | ValidUserDetails>(null);
  const [getUserDetails] = useGetUserDetailsLazyQuery();

  useEffect(() => {
    const getValidatedDetails = async () => {
      try {
        const res = await getUserDetails();
        const parsedData = validateApolloResponse(res.data?.user_details);
        setUserDetails(parsedData);
      } catch {
        // Assume user is logged out
        return setUserDetails(null);
      }
    };
    getValidatedDetails();
  }, [getUserDetails]);

  return userDetails;
};

export const useLazyUserDetails = (): [() => Promise<ValidUserDetails>] => {
  const [getUserDetails] = useGetUserDetailsLazyQuery();

  const getValidatedDetails = async () => {
    const res = await getUserDetails();
    const parsedData = validateApolloResponse(res.data?.user_details);
    return parsedData;
  };

  return [getValidatedDetails];
};

export const UserDetailsProvider: React.FC<{
  children: ReactElement;
  isAuthenticated: boolean;
}> = ({ children, isAuthenticated }) => {
  const {
    data,
    loading,
    refetch: rawRefetch,
  } = useGetUserDetailsQuery({
    fetchPolicy: 'cache-first',
    nextFetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
    // Skip the query if the user is not authenticated or is anonymous
    skip: !isAuthenticated,
  });

  const refetch = useCallback(async () => {
    const { data } = await rawRefetch();
    const parsedData = validateApolloResponse(data.user_details);
    return parsedData;
  }, [rawRefetch]);

  // useOnIdTokenChangedObserver(refetch);
  useOnIdTokenChangedObserver();

  // No provider unless explicitly known auth route, hook will throw error for visibility
  // Post rollout, we can auth using serverProps
  if (!isAuthenticated) {
    return children;
  }

  if (loading || !data?.user_details) return <></>;
  const parsedData = validateApolloResponse(data.user_details);

  return (
    <UserDetailsContext.Provider
      value={{
        userDetails: {
          ...parsedData,
          helpers: {
            isTeamAdmin: getIsTeamAdmin(parsedData),
          },
        },
        refetch,
      }}
    >
      {children}
    </UserDetailsContext.Provider>
  );
};

export const getIsTeamAdmin = (validData: ValidUserDetails): boolean => {
  return (
    validData.current_workspace.type === WorkspaceType.TEAM &&
    [WorkspaceRoleEnum.ADMIN, WorkspaceRoleEnum.OWNER].includes(validData.current_workspace.profile.role)
  );
};
